import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import FoundationLayout from "@/components/FoundationLayout";
import SeoHead from "@/components/SeoHead";
import { serif } from "@/lib/foundationTheme";

/**
 * Catch-all 404. Without this, an unmatched route rendered nothing at
 * all — a blank page with a 200 status, which is bad for both a human
 * who mistyped a URL and for search engines (a "soft 404").
 *
 * `embedded`: when true, skip FoundationLayout (used inside a shell that
 * already provides its own chrome, e.g. the CRM sidebar) and the
 * "back home" link points at that shell's own root instead of "/".
 */
export default function NotFound({ embedded = false, homeHref = "/", homeLabel = "Back home" }) {
  const content = (
    <div className="mx-auto max-w-2xl px-6 lg:px-10 py-24 text-center" data-testid="not-found">
      {/* Inside `content`, not the wrapper below — the embedded variant
          (tenant sites, CRM shell) returns early, and without this a dead
          URL on a customer's site shipped no noindex and kept whatever
          title the previous page had. Soft-404s Google will happily index. */}
      <SeoHead title="Page not found" description="This page doesn't exist." noindex />
      <div className="text-xs uppercase tracking-[0.32em] text-[hsl(34_48%_33%)] mb-6">404</div>
      <h1 className="font-serif-display text-4xl md:text-5xl leading-[1.1] text-[hsl(40_6%_10%)] mb-6">
        This page doesn't exist.
      </h1>
      <p className="text-[hsl(39_8%_33%)] leading-relaxed mb-10">
        The link might be out of date, or the address was mistyped. Nothing to worry about. Let's get you back on track.
      </p>
      <Link
        to={homeHref}
        className="inline-flex items-center gap-2 text-sm text-[hsl(34_48%_33%)] hover:text-[hsl(34_49%_58%)] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> {homeLabel}
      </Link>
    </div>
  );

  if (embedded) return content;

  return (
    <FoundationLayout>
      {content}
    </FoundationLayout>
  );
}
